import { XIcon } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'

/**
 * An option whose chip, once picked, opens a text box inside itself (LG-1030),
 * so the row reads "Other: what they typed" right where they tapped — no
 * field appearing further down the page, and no chip vanishing out from under
 * a finger. The caller owns the text; this owns where it is drawn and the way
 * back out.
 */
export type OtherChip = {
  /** Which option opens the box. */
  option: string
  /** The box's accessible name. */
  label: string
  placeholder: string
  maxLength?: number
  value: string
  onChange: (value: string) => void
}

type ChipGroupProps = {
  label: string
  options: readonly string[]
  disabled?: boolean
  /** Id of help text describing the group, hung on the group itself. */
  describedBy?: string
  /** Whether this group is why the step was refused. */
  invalid?: boolean
  other?: OtherChip
} & (
  | { multi?: false; value: string; onChange: (value: string) => void }
  | { multi: true; value: string[]; onChange: (value: string[]) => void }
)

// Tap targets are 44px tall so a chip is a legitimate touch target on
// phones (item 7); the group wraps rather than scrolling horizontally.
// Exported because the /apply follow-up piles draw the same chip over
// answers keyed by id rather than by label, so they cannot use this
// component — and two hand-kept copies of a 44px tap target is how one of
// them quietly stops being one.
export const CHIP_BASE =
  'inline-flex min-h-11 items-center rounded-full border px-4 py-2 text-sm transition-colors disabled:pointer-events-none disabled:opacity-50 outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px]'
export const CHIP_ON = 'border-primary bg-primary text-primary-foreground'
export const CHIP_OFF = 'border-input bg-background hover:bg-accent'

// The open Other chip: one pill holding the chip, the box and the way out. It
// grows to fill the row it is on and wraps onto a row of its own when there is
// no room, so a long answer never squeezes its neighbours. The ring is drawn
// on the pill, so the box inside must not draw a second one.
const OTHER_PILL =
  'flex min-h-11 grow basis-64 items-center gap-1 rounded-full border border-primary bg-primary pr-1 text-primary-foreground focus-within:ring-[3px] focus-within:ring-ring/50'
// The chip inside that pill, which already draws the border and the fill.
const OTHER_CHIP_OPEN =
  'min-h-0 border-0 bg-transparent py-0 pr-0 text-primary-foreground'

/**
 * Tappable selector used by the /apply question steps in place of native
 * dropdowns. Single-select carries radiogroup semantics (one tab stop, the
 * checked chip owns focus); multi-select is a plain group of toggle buttons
 * with aria-pressed, because a checkbox-style group has no roving-focus
 * convention to honor.
 */
export function ChipGroup(props: ChipGroupProps) {
  const { label, options, disabled, describedBy, invalid, other } = props
  // Declared unconditionally (rules of hooks). Single-select moves its roving
  // tab stop through these; both branches hand focus back to the Other chip
  // through them when its box closes.
  const chipRefs = useRef<(HTMLButtonElement | null)[]>([])
  const otherBox = useRef<HTMLInputElement>(null)
  // Set when a TAP opens the Other box, so the box takes focus once drawn.
  // Only a tap: arrowing through a radiogroup also picks, and pulling focus
  // into the box there would strand the arrow keys halfway along the group.
  const focusOtherBox = useRef(false)
  useEffect(() => {
    if (!focusOtherBox.current) return
    focusOtherBox.current = false
    otherBox.current?.focus()
  })

  /**
   * One chip, wrapped in the Other pill when it is the Other option.
   *
   * The wrapper is there for Other whether or not the box is open — `contents`
   * while closed — so the chip is the same DOM node in both states. Swapping
   * the tree around it instead would remount the chip the moment the box
   * closes, dropping the focus the Remove button just handed back to it.
   */
  const chip = (
    option: string,
    index: number,
    open: boolean,
    button: React.ReactNode,
    unpick: () => void,
  ) => {
    if (!other || option !== other.option) return button
    return (
      <div key={option} className={open ? OTHER_PILL : 'contents'}>
        {button}
        {open && (
          <>
            <input
              ref={otherBox}
              aria-label={other.label}
              placeholder={other.placeholder}
              maxLength={other.maxLength}
              value={other.value}
              disabled={disabled}
              onChange={(e) => other.onChange(e.target.value)}
              className="min-w-0 flex-1 bg-transparent py-2 text-sm outline-none placeholder:text-primary-foreground/60 disabled:opacity-50"
            />
            <button
              type="button"
              aria-label={`Remove ${option}`}
              disabled={disabled}
              onClick={() => {
                unpick()
                chipRefs.current[index]?.focus()
              }}
              // 44px to tap, overlapping the pill's 1px border top and bottom
              // so the pill stays the height of every other chip on its row.
              className="-my-px inline-flex size-11 shrink-0 items-center justify-center rounded-full outline-none hover:bg-primary-foreground/15 focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50"
            >
              <XIcon aria-hidden="true" className="size-4" />
            </button>
          </>
        )}
      </div>
    )
  }

  if (props.multi) {
    const { value, onChange } = props
    return (
      <div
        role="group"
        aria-label={label}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        className="flex flex-wrap gap-2"
      >
        {options.map((option, index) => {
          const on = value.includes(option)
          const open = on && option === other?.option
          // Rebuild from `options` rather than appending, so the stored
          // array always reads in the order the chips are displayed —
          // otherwise deselect/reselect silently reorders the answer.
          const pick = (picked: boolean) =>
            onChange(
              options.filter((o) =>
                o === option ? picked : value.includes(o),
              ),
            )
          return chip(
            option,
            index,
            open,
            <button
              key={option}
              ref={(node) => {
                chipRefs.current[index] = node
              }}
              type="button"
              aria-pressed={on}
              disabled={disabled}
              onClick={() => {
                if (!on && option === other?.option)
                  focusOtherBox.current = true
                pick(!on)
              }}
              className={cn(
                CHIP_BASE,
                on ? CHIP_ON : CHIP_OFF,
                open && OTHER_CHIP_OPEN,
              )}
            >
              {option}
              {open && <span aria-hidden="true">:</span>}
            </button>,
            () => pick(false),
          )
        })}
      </div>
    )
  }

  const { value, onChange } = props

  // The single tab stop: the checked chip, or the first chip when nothing is
  // checked YET — but also when `value` is a stale string no longer in
  // `options` (e.g. a resumed draft saved under since-reworded option copy).
  // Keying the stop off `value === option` alone would give every chip
  // tabIndex -1 in that case, trapping the group out of keyboard reach.
  const selectedIndex = options.indexOf(value)
  const activeIndex = selectedIndex >= 0 ? selectedIndex : 0

  // WAI-ARIA APG radiogroup: arrow keys move the roving tab stop, and per
  // the pattern, moving focus also selects — so both onChange and the DOM
  // focus target must move together.
  const moveTo = (index: number) => {
    const targetIndex = (index + options.length) % options.length
    const target = options[targetIndex]
    if (target === undefined) return
    onChange(target)
    chipRefs.current[targetIndex]?.focus()
  }

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault()
        moveTo(index + 1)
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault()
        moveTo(index - 1)
        break
      case 'Home':
        event.preventDefault()
        moveTo(0)
        break
      case 'End':
        event.preventDefault()
        moveTo(options.length - 1)
        break
      default:
        break
    }
  }

  return (
    <div
      role="radiogroup"
      aria-label={label}
      aria-describedby={describedBy}
      aria-invalid={invalid || undefined}
      className="flex flex-wrap gap-2"
    >
      {options.map((option, index) => {
        const on = value === option
        const open = on && option === other?.option
        return chip(
          option,
          index,
          open,
          <button
            key={option}
            ref={(node) => {
              chipRefs.current[index] = node
            }}
            type="button"
            role="radio"
            aria-checked={on}
            // Roving tabindex: exactly one chip is the tab stop (see
            // activeIndex above), so the group is always reachable.
            tabIndex={index === activeIndex ? 0 : -1}
            disabled={disabled}
            onClick={() => {
              if (!on && option === other?.option) focusOtherBox.current = true
              onChange(option)
            }}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={cn(
              CHIP_BASE,
              on ? CHIP_ON : CHIP_OFF,
              open && OTHER_CHIP_OPEN,
            )}
          >
            {option}
            {open && <span aria-hidden="true">:</span>}
          </button>,
          () => onChange(''),
        )
      })}
    </div>
  )
}
